import { useState, useEffect, useMemo, useRef } from 'react';
import { doc, updateDoc, increment, setDoc, getDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { fireDB as db, appId } from '../../../services/firebase';
import { playSound } from '../../../utils/helpers';
import { LAW_CONFIG, LAW_DATA } from '../../../data/laws';

export const useGameLogic = ({ user, userData, setView, selectedLaw, selectedStage, setSelectedStage, gameModeType, onHome }) => {
  const sessionKey = `law_game_idx_${user?.uid}_${selectedLaw}_${selectedStage}_${gameModeType}`;

  const [mode, setMode] = useState(gameModeType === 'challenge' ? 'expert' : null);
  const [questions, setQuestions] = useState([]);
  
  const [qIndex, setQIndex] = useState(() => {
    const saved = sessionStorage.getItem(sessionKey);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [lives, setLives] = useState(7); 
  const [maxLives, setMaxLives] = useState(7);
  const [gameState, setGameState] = useState(gameModeType === 'challenge' ? 'playing' : 'mode-select'); 
  const [expertStep, setExpertStep] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isCurrentBookmarked, setIsCurrentBookmarked] = useState(false);
  const [timer, setTimer] = useState(15);
  const [challengeStage, setChallengeStage] = useState(1);
  const inputRef = useRef(null);
  const [currentChoices, setCurrentChoices] = useState([]);

  const isLoaded = useRef(false);

  const currentQ = questions[qIndex];
  const targetKeywordObj = useMemo(() => {
    if (!currentQ || !currentQ.keywords || currentQ.keywords.length === 0) return null;
    return currentQ.keywords[0]; 
  }, [currentQ]);

  useEffect(() => {
    sessionStorage.setItem(sessionKey, qIndex.toString());
  }, [qIndex, sessionKey]);

  const clearSession = () => {
    sessionStorage.removeItem(sessionKey);
  };

  // --- 다음 문제 이동 ---
  const nextQuestionOrStage = () => {
    setFeedback(null); 
    setInputValue(''); 
    setExpertStep(0);
    
    // 다음 문제로 넘어갈 때 타이머 리셋
    if (gameModeType === 'challenge') {
         const timeLimit = challengeStage === 1 ? 20 : challengeStage === 2 ? 16 : 12;
         setTimer(timeLimit);
    } else {
         setTimer(15);
    }

    if (qIndex + 1 < questions.length) { 
      setQIndex(prev => prev + 1); 
    } else {
      clearSession(); 
      if (gameModeType === 'challenge') {
        if (challengeStage < 3) { 
          setChallengeStage(prev => prev + 1); 
          setQIndex(0); 
          isLoaded.current = false;
          setQuestions([]); 
          alert(`Stage ${challengeStage} 클리어! 다음 단계로 넘어갑니다.`); 
        } else { 
          setGameState('clear'); 
        }
      } else { 
        setGameState('clear'); 
      }
    }
  };

  const handleCorrect = async () => {
    playSound('correct'); 
    setFeedback('correct');
    try {
      if (user && user.uid) {
        await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'info'), { 
          progress: increment(1), 
          totalSolved: increment(1), 
          correctCount: increment(1) 
        });
      }
    } catch(e) {}
    setTimeout(() => { nextQuestionOrStage(); }, 1800); 
  };

  // --- 오답 처리 ---
  const handleWrong = async (isTimeout = false) => {
    playSound('wrong'); 
    setFeedback('wrong');
    const newLives = lives - 1; 
    setLives(newLives);
    
    if (gameModeType !== 'odaptong_retry') {
        const newLives = lives - 1; 
        setLives(newLives);
        
        if (newLives <= 0) { 
          clearSession();
          setTimeout(() => setGameState('game-over'), 1500); 
          return; 
        }
    }
    
    if (newLives <= 0) { 
      clearSession();
      setTimeout(() => setGameState('game-over'), 1500); 
      return; 
    }
    
    setTimeout(() => {
        setFeedback(null); 
        setInputValue('');
        
        if (gameModeType === 'challenge') {
             // 챌린지 모드: 시간 초과 시 다음 문제, 아니면 단계만 증가(타이머 유지)
             if (isTimeout) {
                 nextQuestionOrStage();
             } else {
                 if (expertStep < 2) {
                     setExpertStep(prev => prev + 1);
                 } else {
                     nextQuestionOrStage();
                 }
             }
        } else {
             // 일반 모드
             setTimer(15);
             if (mode === 'expert') {
                if (expertStep < 2) { setExpertStep(prev => prev + 1); } 
                else { setExpertStep(0); nextQuestionOrStage(); }
             } else { 
                nextQuestionOrStage(); 
             }
        }
    }, 1800); 
  };

  // [핵심 수정] 타이머가 실행될 때 항상 최신 상태의 handleWrong을 참조하도록 Ref 사용
  // 이렇게 하면 첫 문제 로딩 직후에도 타이머가 'questions'가 채워진 최신 handleWrong을 실행합니다.
  const handleWrongRef = useRef();
  useEffect(() => {
    handleWrongRef.current = handleWrong;
  });

  const submitExpertInput = (e) => {
    if (e) e.preventDefault();
    if (!targetKeywordObj) return;

    // 정규식 설명: /[\s·]/g
    // \s : 모든 공백(띄어쓰기, 탭 등)
    // ·  : 가운뎃점 특수문자
    // 이 두 가지를 찾아서 모두 빈 문자열('')로 바꿔버립니다.
    const cleanInput = inputValue.replace(/[\s·]/g, '');
    const cleanTarget = targetKeywordObj.word.replace(/[\s·]/g, '');

    // 이제 "시·도지사"와 "시도지사"는 둘 다 "시도지사"가 되어 정답 처리됩니다.
    if (cleanInput === cleanTarget) { 
        handleCorrect(); 
    } else { 
        handleWrong(); 
    }
  };

  const submitChoice = (choice) => {
    if (!targetKeywordObj) return;
    if (choice === targetKeywordObj.word) { handleCorrect(); } else { handleWrong(); }
  };

  useEffect(() => {
    if (targetKeywordObj) {
      const newChoices = [targetKeywordObj.word, targetKeywordObj.distractor].sort(() => Math.random() - 0.5);
      setCurrentChoices(newChoices);
    } else { setCurrentChoices([]); }
  }, [targetKeywordObj]);

  // --- 초기화 로직 ---
  useEffect(() => {
    if (questions.length > 0 && gameModeType !== 'challenge') return; 
    if (isLoaded.current && gameModeType !== 'challenge') return;

    const initQuestions = async () => {
        let qData = [];
        const config = LAW_CONFIG[selectedLaw];
        
        if (config) { qData = [...(config.data || [])]; } 
        else { qData = [...LAW_DATA]; }

        if (gameModeType === 'odaptong_retry') {
            const wrongRef = collection(db, 'artifacts', appId, 'users', user.uid, 'wrong_answers');
            const snap = await getDocs(wrongRef);
            const wrongIds = [];
            snap.forEach(doc => {
                const d = doc.data();
                if (d.count >= 3 || d.isBookmarked) { wrongIds.push(d.id); }
            });
            qData = qData.filter(q => wrongIds.includes(q.id));
            if (qData.length === 0) { alert("오답통에 문제가 없습니다."); setView('odaptong'); return; }
            qData = qData.sort(() => 0.5 - Math.random());
            setMode('expert');
            setGameState('mode-select'); 

        } else if (gameModeType === 'challenge') {
            const allData = [
                ...LAW_DATA, 
                ...(LAW_CONFIG['손실보상']?.data || []), 
                ...(LAW_CONFIG['부동산공시법']?.data || []), 
                ...(LAW_CONFIG['감정평가법']?.data || []), 
                ...(LAW_CONFIG['헌법 및 헌법재판소법']?.data || []),
                ...(LAW_CONFIG['행정기본법']?.data || []),
                ...(LAW_CONFIG['행정심판법']?.data || []),
                ...(LAW_CONFIG['행정소송법']?.data || []),
                ...(LAW_CONFIG['행정절차법']?.data || [])
            ];
            qData = allData.sort(() => 0.5 - Math.random());
            if(qData.length > 30) { qData = qData.slice(0, 30); }

        } else {
            const targetStage = parseInt(selectedStage, 10);
            const maxStage = config ? config.maxStage : -1;

            if (targetStage === maxStage) {
                qData = qData.sort(() => 0.5 - Math.random());
                if (qData.length > 30) { qData = qData.slice(0, 30); }
            } 
            else if (targetStage) {
                qData = qData.filter(q => q.stage === targetStage)
                             .sort(() => 0.5 - Math.random());
            } 
            else {
                qData = qData.sort(() => 0.5 - Math.random());
            }
        }
        
        setQuestions(qData);
        const saved = sessionStorage.getItem(sessionKey);
        if (!saved) setQIndex(0); 
        
        setLives(7);
        if (typeof setMaxLives === 'function') setMaxLives(7);
        setFeedback(null);
        setInputValue('');
        isLoaded.current = true;
    };
    initQuestions();
  }, [gameModeType, user.uid, selectedLaw, selectedStage, challengeStage, sessionKey]); 

  // --- 타이머 작동 (Ref 사용으로 버그 해결) ---
  useEffect(() => {
    if (gameModeType !== 'challenge' || gameState !== 'playing') return;
    if (feedback !== null) return; 

    const interval = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // [수정] Ref를 통해 최신 handleWrong 함수 호출
          if (handleWrongRef.current) {
            handleWrongRef.current(true);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [qIndex, gameState, feedback, gameModeType]);

  // 첫 진입 시 타이머 세팅 및 북마크 확인
  useEffect(() => {
    if (!currentQ) return;

    if (qIndex === 0 && expertStep === 0) {
        if (gameModeType === 'challenge') {
            const timeLimit = challengeStage === 1 ? 20 : challengeStage === 2 ? 16 : 12;
            setTimer(timeLimit); 
        } else {
            setTimer(15); 
        }
    }

    const checkBookmark = async () => {
      try {
        const docRef = doc(db, 'artifacts', appId, 'users', user.uid, 'wrong_answers', currentQ.id.toString());
        const snapshot = await getDoc(docRef);
        setIsCurrentBookmarked(snapshot?.exists() ? snapshot.data().isBookmarked : false); 
      } catch (e) {}
    };
    checkBookmark();
    setTimeout(() => { if(inputRef.current) inputRef.current.focus(); }, 100);
  }, [currentQ, challengeStage, gameModeType, user.uid, qIndex]); 

  const toggleBookmark = async () => {
    if (!currentQ) return; 
    const newState = !isCurrentBookmarked;
    setIsCurrentBookmarked(newState);
    try {
      const docRef = doc(db, 'artifacts', appId, 'users', user.uid, 'wrong_answers', currentQ.id.toString());
      await setDoc(docRef, { id: currentQ.id, article: currentQ.article, isBookmarked: newState, lastUpdated: serverTimestamp() }, { merge: true });
    } catch(e) {}
  };

  const handleRetry = () => {
    clearSession();
    const shuffled = [...questions].sort(() => 0.5 - Math.random());
    setQuestions(shuffled); 
    setQIndex(0); 
    setLives(7); 
    setGameState('playing'); 
    setFeedback(null); 
    setInputValue(''); 
    setExpertStep(0);
    if(gameModeType === 'challenge') setChallengeStage(1);
    
    const initialTime = gameModeType === 'challenge' ? 20 : 15;
    setTimer(initialTime);
  };

  const handleToExpert = () => {
    clearSession();
    setMode('expert'); 
    setQIndex(0); 
    setLives(7);
    setGameState('playing'); 
    setExpertStep(0);
    const shuffled = [...questions].sort(() => 0.5 - Math.random());
    setQuestions(shuffled);
    
    const initialTime = gameModeType === 'challenge' ? 20 : 15;
    setTimer(initialTime);
  };

  const handleNextStage = (nextId) => {
    clearSession();
    setSelectedStage(nextId);
    setQIndex(0);
    setQuestions([]); 
    isLoaded.current = false;
    setGameState('mode-select'); 
  };

  const handleBackStep = () => {
    clearSession();
    if (gameModeType === 'challenge') { onHome(); } 
    else if (gameModeType === 'odaptong_retry') { setView('odaptong'); } 
    else if (selectedStage) { setView('stage_select'); } 
    else { setGameState('mode-select'); }
  };

  return {
    state: {
        mode, questions, qIndex, lives, maxLives, gameState,
        expertStep, inputValue, feedback, isCurrentBookmarked,
        timer, challengeStage, currentChoices
    },
    refs: { inputRef },
    data: { currentQ, targetKeywordObj },
    actions: {
        setMode, setGameState, setInputValue,
        handleRetry, handleToExpert, handleNextStage, handleBackStep,
        submitExpertInput, submitChoice, toggleBookmark
    }
  };
};